import { forwardRef } from 'react';
import type { CSSProperties, ForwardedRef, ReactNode } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import type { BodyItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/bodyItemAttributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';

interface DSSectionProps {
  attributes: SectionItemAttributes;
  bodyWidth: BodyItemAttributes['bodyWidth'];
  children: ReactNode;
  gridStyle: CSSProperties;
  wrapperStyle: CSSProperties;
}

const DSSection = forwardRef(
  (
    {
      attributes,
      bodyWidth,
      children,
      gridStyle,
      wrapperStyle,
    }: DSSectionProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const breakpoint = useBreakpoint();

    return (
      <WrapperInner
        data-testid="ds-section-wrapper"
        $sectionWidth={attributes.sectionWidth}
        style={wrapperStyle}
      >
        <Grid
          {...(bodyWidth === 'auto' && { mx: 0 })}
          data-testid="ds-section-grid"
          ref={ref}
          // @ts-expect-error no "style" type on the Grid component
          style={gridStyle}
          width="100%"
          mt={0}
        >
          <Row justifyCenter>
            <Col
              overrideRowGap={-6}
              width={attributes?.numCols?.[breakpoint] || 1}
            >
              {children}
            </Col>
          </Row>
        </Grid>
      </WrapperInner>
    );
  },
);

export default DSSection;

interface WrapperInnerProps {
  $sectionWidth: SectionItemAttributes['sectionWidth'];
}

const WrapperInner = styled(Box).attrs(
  ({ $sectionWidth }: WrapperInnerProps) => ({
    alignSelf: 'center',
    height: '100%',
    maxWidth: $sectionWidth,
    width: '100%',
  }),
)<WrapperInnerProps>`
  margin: 0 auto;

  svg {
    // override backoffice rule
    max-width: inherit !important;
  }
`;
